import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useShow } from '@refinedev/core'
import { useParams } from 'react-router-dom'

import { useAppointmentEmailsQuery, useAppointmentHistoryQuery } from '../api'
import { isTestAppointment } from '../appointment.utils.ts'
import type { Appointment} from '../types';
import { APPOINTMENT_STATUS_LABELS } from '../types'

import ActiveAppointmentActions from './ActiveAppointmentActions'
import { ClosedAppointmentActions } from './ClosedAppointmentActions/ClosedAppointmentActions.tsx'
import { getStatusColor } from './config.ts'
import ScoutAssignments from './ScoutAssignments'
import ScoutRatingsSection from './ScoutRatingsSection'
import SectionAppointment from './SectionAppointment'
import SectionBuilding from './SectionBuilding'
import SectionCustomer from './SectionCustomer'
import SectionCustomerProposedDates from './SectionCustomerProposedDates'
import SectionDataCollection from './SectionDataCollection'
import SectionEmailHistory from './SectionEmailHistory'
import SectionSales from './SectionSales'
import SectionSchedule from './SectionSchedule'
import SectionScout from './SectionScout'
import { chipStyle, colorsStyle } from './style.ts'

import { CommentSection } from '@/shared/components/CommentSection'
import History from '@/shared/components/History'

export const AppointmentDetails = () => {
  const { id } = useParams<{ id: string }>()

  // Simple back navigation using browser history
  const handleGoBack = () => {
    window.history.back()
  }

  const { queryResult } = useShow<Appointment>({
    resource: 'appointments',
    id: id as string,
  })
  const { data, isLoading, refetch } = queryResult

  const appointment = data?.data

  const { data: historyData, isLoading: historyLoading } = useAppointmentHistoryQuery(
    appointment?.uuid || '',
    {
      enabled: !!appointment?.uuid,
    }
  )

  const { data: emailsData, isLoading: emailsLoading } = useAppointmentEmailsQuery(
    appointment?.uuid || '',
    { enabled: !!appointment?.uuid }
  )

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={handleGoBack} variant="outlined" size="small">
          Back to List
        </Button>
        <Typography variant="h4" component="h1">
          Appointment Details
        </Typography>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      )}

      {!appointment && !isLoading && (
        <Box p={3}>
          <Typography variant="h6" color="text.secondary">
            Appointment not found
          </Typography>
        </Box>
      )}
      {!isLoading && appointment && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack spacing={3}>
                {/* Header with ID and Status */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h5" component="h2">
                      Appointment Details
                    </Typography>
                    <Chip
                      label={APPOINTMENT_STATUS_LABELS[appointment.status]}
                      color={getStatusColor(appointment.status)}
                      size="medium"
                    />
                    <Chip
                      label={appointment.type === 'isfp' ? 'iSFP' : 'Marketplace'}
                      color={appointment.type === 'isfp' ? 'primary' : 'default'}
                      variant="outlined"
                      size="medium"
                    />
                    {/* Only meaningful for marketplace appointments. Colors match
                    the subtype chips in scout-app's appointment tile. */}
                    {appointment.subtypes?.map(subtype => (
                      <Chip
                        key={subtype}
                        label={subtype === 'pv' ? 'Photovoltaic' : 'Heatpump'}
                        size="medium"
                        sx={subtype === 'pv' ? colorsStyle.pv : colorsStyle.hp}
                      />
                    ))}
                    {isTestAppointment(appointment) && (
                      <Chip
                        label="⚠️ TEST APPOINTMENT"
                        variant="filled"
                        color="warning"
                        size="medium"
                        sx={chipStyle}
                      />
                    )}
                  </Box>

                  {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                    <ActiveAppointmentActions appointment={appointment} onRefetch={refetch} />
                  )}

                  {(appointment.status === 'cancelled' || appointment.status === 'completed') && (
                    <ClosedAppointmentActions appointment={appointment} />
                  )}
                </Box>

                <Divider />

                {/* Main Details Grid */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <SectionCustomer appointment={appointment} />
                  <SectionAppointment appointment={appointment} onRefetch={refetch} />
                  <SectionBuilding appointment={appointment} />
                  <SectionSchedule appointment={appointment} />
                  <SectionCustomerProposedDates appointment={appointment} />
                  <SectionScout appointment={appointment} />
                  <SectionDataCollection appointment={appointment} />
                  <SectionSales appointment={appointment} />

                  {/* Scout Ratings */}
                  {appointment?.uuid && appointment.status === 'completed' && (
                    <Box sx={{ flex: '1 1 100%' }}>
                      <ScoutRatingsSection appointmentId={appointment.uuid} />
                    </Box>
                  )}

                  {/* Comments Section */}
                  {appointment?.uuid && (
                    <Box sx={{ flex: '1 1 100%' }}>
                      <CommentSection objectUuid={appointment.uuid} />
                    </Box>
                  )}

                  <ScoutAssignments appointment={appointment} />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <SectionEmailHistory emails={emailsData?.data} isLoading={emailsLoading} />

          <History
            history={historyData?.data}
            isLoading={historyLoading}
            title="Appointment Changelog"
          />
        </Box>
      )}
    </Box>
  )
}
